import { loadConfig, saveConfig } from './config.js';
import { getResolvedToken, setRecToken, setOAuthTokens, clearAllTokens, type ResolvedToken } from './token-store.js';
import {
  refreshAccessToken,
  expiresInToIso,
  getAuthKitDomain,
  getAuthKitClientId,
  SessionExpiredError,
  decodeUnverifiedIssuer,
  issuerMatchesConfiguredEnv,
} from './oauth.js';

/** Persist a pasted rec_ token (CI / headless path). */
export async function login(token: string, apiUrl?: string): Promise<void> {
  await setRecToken(token);
  const config = loadConfig();
  saveConfig({
    ...config,
    api_url: apiUrl ?? config.api_url,
  });
}

const REFRESH_LEEWAY_MS = 60_000;

// Warn when a cached OAuth access token was minted for a different WorkOS environment than the
// CLI is configured for. The backend rejects such a token as "issuer not recognized" (a 401 the
// user can't act on without knowing the cause), and `rekor login` is the only fix — but nothing
// tells them that, so a stale login left in the keychain after switching environments fails
// silently. Surfacing it here turns the opaque 401 into one actionable line. Diagnostics only:
// it never blocks the request (the backend stays the authority) and an undecodable token (or a
// matching issuer) skips it. Fires once per invocation; that's intended — it should keep nagging
// until re-login, and it only triggers in the already-broken state.
function warnIfIssuerMismatch(accessToken: string): void {
  const iss = decodeUnverifiedIssuer(accessToken);
  if (iss === undefined) return;
  const domain = getAuthKitDomain();
  if (issuerMatchesConfiguredEnv(iss, domain, getAuthKitClientId())) return;
  console.warn(
    `rekor: your saved login is for a different WorkOS environment than the CLI is configured for ` +
    `(token issuer "${iss}", expected "${domain}"). Requests will likely be rejected as ` +
    '"issuer not recognized" — run `rekor login` to re-authenticate against the current environment.',
  );
}

// Returns a valid access token; silently refreshes OAuth tokens within 60s of expiry.
export async function getAccessToken(): Promise<string | null> {
  const resolved = await getResolvedToken();
  if (!resolved) return null;

  if (resolved.kind === 'rec') return resolved.token;

  // OAuth path — refresh if near expiry. Missing/unparseable expires_at: assume valid, let the API surface a real 401.
  const expiresAtMs = resolved.expires_at ? new Date(resolved.expires_at).getTime() : Number.NaN;
  if (!Number.isFinite(expiresAtMs) || expiresAtMs > Date.now() + REFRESH_LEEWAY_MS) {
    warnIfIssuerMismatch(resolved.access_token);
    return resolved.access_token;
  }
  if (!resolved.refresh_token) {
    throw new Error('Access token expired and no refresh token available. Run `rekor login` again.');
  }

  try {
    const result = await refreshAccessToken(getAuthKitDomain(), getAuthKitClientId(), resolved.refresh_token);
    const newExpiresAt = expiresInToIso(result.expires_in);
    await setOAuthTokens(result.access_token, result.refresh_token ?? resolved.refresh_token, newExpiresAt);
    warnIfIssuerMismatch(result.access_token);
    return result.access_token;
  } catch (err) {
    if (err instanceof SessionExpiredError) {
      await clearAllTokens();
      throw new Error('Session expired. Run `rekor login` again.');
    }
    throw err;
  }
}

/** Auth kind for the current credential, without resolving the token itself. Used by logout to decide if it should call the revoke endpoint. */
export async function currentAuthKind(): Promise<ResolvedToken['kind'] | null> {
  const resolved = await getResolvedToken();
  return resolved?.kind ?? null;
}

export async function isAuthenticated(): Promise<boolean> {
  return (await getResolvedToken()) !== null;
}
